import { CancelOutlined, CheckCircleOutlined, KeyboardArrowDown, Loop } from "@mui/icons-material";
import { Box, CSSObject, Menu, MenuItem, Tooltip } from "@mui/material";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { enumToArray } from "../../../common/api/helper";
import { ApplicationUser } from "../../users/User.types";
import { Case, RenewalStatus } from "../Case.types";
import { updateRenewalStatus } from "../CaseService";
import RenewalRemarkModal from "../RenewalRemarkModal";
import Rotatable from "../Rotateable/Rotatable";
import { Editability, getEditabilityTooltip, getRenewalEditability } from "./renewalEditability";

export type CaseWithMandatoryRenewalStatus = Omit<Case, "renewalStatus"> & { renewalStatus: RenewalStatus };
export interface RenewalStatusWidgetProps {
    selectedCase: CaseWithMandatoryRenewalStatus;
    onUpdatedCase?: (updatedCase: Case) => void;
    editSetRenewalStatus: boolean; //this is true for users with employee or admin privileges
    caseType?: string;
    verifiedUser: ApplicationUser | null;
}

function getEnumIcon(status: RenewalStatus) {
    switch (status) {
        case RenewalStatus.Awaiting:
            return <Loop fontSize={"small"} />;
        case RenewalStatus.Expire:
            return <CancelOutlined fontSize={"small"} />;
        case RenewalStatus.Renew:
            return <CheckCircleOutlined fontSize={"small"} />;
    }
}

function getEnumCSS(status: RenewalStatus, disabled?: boolean): CSSObject {
    switch (status) {
        case RenewalStatus.Expire:
            return { border: "1px solid #fa3a75", backgroundColor: "#fec2d5", color: "#fa3a75" };
        case RenewalStatus.Awaiting:
            return { border: "1px solid #7D7365", backgroundColor: "#EFEBE5", color: "#4A443C" };
        case RenewalStatus.Renew:
            return { border: "1px solid #6F7AC4", backgroundColor: "#E4EAFF", color: "#6F7AC4" };
    }
}

function getMenuButtonText(status: RenewalStatus) {
    switch (status) {
        case RenewalStatus.Expire:
            return "Expire";
        case RenewalStatus.Awaiting:
            return "Awaiting";
        case RenewalStatus.Renew:
            return "Renew";
    }
}

function getStatusPickerContent(status: RenewalStatus, isMenuPicker?: boolean, rightIcon?: JSX.Element, canEditStatus?: Editability) {
    return (
        <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            gap={(theme) => theme.spacing(1)}
            paddingX={(theme) => theme.spacing(1)}
            sx={(theme) =>
                ({
                    fontSize: "12px",
                    borderRadius: "15px",
                    minWidth: "120px",
                    ...getEnumCSS(status),
                    opacity: canEditStatus === Editability.Editable ? 1 : 0.5,
                } as CSSObject)
            }
        >
            {getEnumIcon(status)}
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                {getMenuButtonText(status)}
                {rightIcon && canEditStatus === Editability.Editable ? rightIcon : <Box />}
            </Box>
        </Box>
    );
}

const RenewalStatusWidget: FunctionComponent<RenewalStatusWidgetProps> = (props) => {
    const [open, setOpen] = useState<boolean>(false);
    const [status, setStatus] = useState<RenewalStatus>(props.selectedCase.renewalStatus ?? RenewalStatus.Awaiting);
    const [newStatus, setNewStatus] = useState<RenewalStatus>();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [remarkOpen, setRemarkOpen] = useState<boolean>(false);
    const [isNewExpireOrRenew, setIsNewExpireOrRenew] = useState<boolean>(false);
    const canEditStatus = useMemo(
        () => getRenewalEditability(props.selectedCase, props.editSetRenewalStatus, props.verifiedUser),
        [props.selectedCase, props.editSetRenewalStatus, props.verifiedUser]
    );

    const saveStatusMutation = useMutation(updateRenewalStatus, {
        onSuccess: (data) => {
            const newCases = data.value();
            if (props.onUpdatedCase && newCases && newCases.length > 0) {
                newCases.forEach((x) => {
                    props.onUpdatedCase?.(x);
                });
            }
        },
    });

    useEffect(() => {
        if (props.selectedCase.internalId && newStatus === RenewalStatus.Awaiting)
            saveStatusMutation.mutateAsync({ internalId: props.selectedCase.internalId, status: status }).then();
        // eslint-disable-next-line
    }, [status]);

    useEffect(() => {
        setStatus(props.selectedCase.renewalStatus ?? RenewalStatus.Awaiting);
    }, [props.selectedCase.renewalStatus]);

    useEffect(() => {
        if (canEditStatus === Editability.DisabledWatches) {
            setStatus(RenewalStatus.Renew);
        }
    }, [canEditStatus]);

    const tooltip = useMemo(() => getEditabilityTooltip(canEditStatus, props.selectedCase), [canEditStatus, props.selectedCase]);

    return (
        <>
            <Tooltip title={tooltip} sx={(theme) => ({ fontSize: "12px" } as CSSObject)}>
                <Box
                    sx={(theme) => ({ fontSize: "12px" } as CSSObject)}
                    onClick={(event) => {
                        if (canEditStatus === Editability.Editable) setOpen(true);
                        setAnchorEl(event.currentTarget);
                    }}
                >
                    {props.selectedCase.isCalculatedRenewal ? (
                        <Box sx={{ minWidth: "120px" }} />
                    ) : (
                        getStatusPickerContent(
                            status,
                            true,
                            <Rotatable isRotated={open}>
                                <KeyboardArrowDown fontSize={"small"} />
                            </Rotatable>,
                            canEditStatus
                        )
                    )}
                </Box>
            </Tooltip>

            <Menu onClose={() => setOpen(false)} open={open} anchorEl={anchorEl} autoFocus={false}>
                {enumToArray(RenewalStatus)
                    .filter((s) => status !== s)
                    .map((status) => {
                        return (
                            <MenuItem
                                key={status.toString()}
                                onClick={() => {
                                    setNewStatus(status);
                                    if (status === RenewalStatus.Awaiting) setStatus(status);
                                    else {
                                        setIsNewExpireOrRenew(true);
                                        setRemarkOpen(true);
                                    }
                                    setOpen(false);
                                }}
                            >
                                {getStatusPickerContent(status, undefined, undefined, Editability.Editable)}
                            </MenuItem>
                        );
                    })}
            </Menu>
            {remarkOpen && (
                <RenewalRemarkModal
                    internalId={props.selectedCase.internalId}
                    remarkOpen={remarkOpen}
                    setRemarkOpen={setRemarkOpen}
                    isNewExpireOrRenew={isNewExpireOrRenew}
                    newStatus={newStatus}
                    setStatus={setStatus}
                    onCaseUpdated={props.onUpdatedCase}
                    caseType={props.caseType}
                />
            )}
        </>
    );
};

export default RenewalStatusWidget;
